import React from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import {
  COLOR_WHITE,
  COLOR_WHITE_ALPHA_40,
  COLOR_WHITE_ALPHA_75,
  COLOR_WHITE_ALPHA_95,
} from "../common/colors";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { DataHandling } from "./app_flow/DataHandling";
import { Upgrade } from "./app_flow/Upgrade";
import { SignUp } from "./app_flow/SignUp";
import { PurchaseChoice } from "./app_flow/PurchaseChoice";
import { ConfirmationCode } from "./app_flow/ConfirmationCode";
import { GrownupsHurdle } from "./app_flow/GrownupsHurdle";
import { isOnFreePass, isUserSignedUp, StripeSubscriptionProductId } from "../common/api";
import {
  KEY_CHECKOUT_SESSION_ID,
  StripeCheckout,
} from "./app_flow/StripeCheckout";

export const enum AppFlowStage {
  Upgrade = 1,
  GrownupsHurdle = 2,
  SignUp = 3,
  ConfirmationCode = 4,
  DataHandling = 5,
  PurchaseChoice = 6,
  Done = 7,
  StripeCheckout = 8,
}

export interface IndexedAppFlowStage {
  stage: AppFlowStage;
  index: number;
  previousStage: AppFlowStage | null;
}

export const UPGRADE_STAGES = [
  AppFlowStage.SignUp,
  AppFlowStage.ConfirmationCode,
  AppFlowStage.PurchaseChoice,
  AppFlowStage.StripeCheckout,
  AppFlowStage.Done,
];

export const enum AppFlowInnerMode {
  Upgrade = 1,
}

export interface AppFlowModeConfig {
  stages: AppFlowStage[];
  profileId?: number;
  selectedTeacherId?: number;
  destinations: string[];
}

export const DEFAULT_UPGRADED_USER_DESTINATION = "/";

export interface AppFlowMode {
  config: AppFlowModeConfig;
  mode: AppFlowInnerMode;
}

const VerticalContainer = styled.div`
  position: relative;
  display: block;
  overflow: hidden;

  width: 35%;
  height: 100%;

  &::before {
    // This is the white gradient overlay on the lesson_cover_grid image
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to left,
      ${COLOR_WHITE_ALPHA_40} 0%,
      ${COLOR_WHITE_ALPHA_75} 50%,
      ${COLOR_WHITE_ALPHA_95} 90%,
      ${COLOR_WHITE} 98%
    );
    pointer-events: none;
  }
`;
const FooterContainer = styled.div`
  position: relative;
  display: block;
  overflow: hidden;

  max-height: 250px;
  width: 100vw;
  margin-left: calc(-50vw + 50%);

  &::before {
    // This is the white gradient overlay on the lesson_cover_grid image
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to top,
      ${COLOR_WHITE_ALPHA_40} 0%,
      ${COLOR_WHITE_ALPHA_75} 50%,
      ${COLOR_WHITE_ALPHA_95} 90%,
      ${COLOR_WHITE} 98%
    );
    pointer-events: none;
  }
`;

export const AppFlowPage: React.FC<{ appFlowMode: AppFlowMode }> = ({
  appFlowMode,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [currentStage, setCurrentStage] = React.useState<IndexedAppFlowStage>();
  const [account, setAccount] = React.useState<Account>();
  const { accountLiveData } = React.useContext(AccountContext);
  const [selectedProductId, setSelectedProductId] =
    React.useState<StripeSubscriptionProductId>();
  const [needToRestart, setNeedToRestart] = React.useState<boolean>(false);
  const [needToExit, setNeedToExit] = React.useState<boolean>(false);
  const [existingCheckoutSessionId, setExistingCheckoutSessionId] =
    React.useState<string>();

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const checkoutSessionId = searchParams.get(KEY_CHECKOUT_SESSION_ID);
    if (checkoutSessionId) {
      setExistingCheckoutSessionId(checkoutSessionId);
    }
  }, [location.search, setExistingCheckoutSessionId]);

  const restartUpgrade = React.useCallback(() => {
    // Assume csid is the only query param we can have. If that changes, fix this code.
    // Remove checkout session id from the URL so that we don't reuse it again.
    history.replace(history.location.pathname);
    // Wipe the session id state right away so that `shouldSkipStage` gets bound to it right away
    setExistingCheckoutSessionId(undefined);
    setNeedToRestart(true);
  }, [history]);

  const shouldSkipStage = React.useCallback(
    (stage: AppFlowStage): boolean => {
      const user = account?.user;
      if (user === undefined) {
        return false;
      }

      // We need to get back to Stripe checkout to continue the checkout session.
      // Speedrun the appflow all the way to checkout, but only if we have a signed up user.
      if (
        existingCheckoutSessionId &&
        isUserSignedUp(user) &&
        stage !== AppFlowStage.StripeCheckout &&
        stage !== AppFlowStage.Done
      ) {
        return true;
      }

      if (stage === AppFlowStage.Upgrade && user.has_unconfirmed_email) {
        return true;
      }
      if (stage === AppFlowStage.GrownupsHurdle && user.has_unconfirmed_email) {
        return true;
      }

      if (
        stage === AppFlowStage.SignUp &&
        (isUserSignedUp(user) || user.has_unconfirmed_email)
      ) {
        return true;
      }

      if (
        stage === AppFlowStage.ConfirmationCode &&
        !user.has_unconfirmed_email
      ) {
        return true;
      }

      if (stage === AppFlowStage.StripeCheckout && isOnFreePass(user)) {
        return true;
      }

      return false;
    },
    [account, existingCheckoutSessionId]
  );

  const previous = React.useCallback(() => {
    let previousIndex = currentStage!!.index - 1;
    while (
      previousIndex > 0 &&
      shouldSkipStage(appFlowMode.config.stages[previousIndex])
    ) {
      previousIndex--;
    }
    setCurrentStage({
      stage: appFlowMode.config.stages[previousIndex],
      index: previousIndex,
      previousStage: currentStage!!.stage,
    });
  }, [
    currentStage,
    setCurrentStage,
    shouldSkipStage,
    appFlowMode.config.stages,
  ]);

  const next = React.useCallback(() => {
    let nextIndex = currentStage!!.index + 1;
    while (
      nextIndex < appFlowMode.config.stages.length - 1 &&
      shouldSkipStage(appFlowMode.config.stages[nextIndex])
    ) {
      nextIndex++;
    }
    setCurrentStage({
      stage: appFlowMode.config.stages[nextIndex],
      index: nextIndex,
      previousStage: currentStage!!.stage,
    });
  }, [
    currentStage,
    setCurrentStage,
    shouldSkipStage,
    appFlowMode.config.stages,
  ]);

  const getFirstStage = React.useCallback((): IndexedAppFlowStage => {
    let index = 0;
    let firstStage = appFlowMode.config.stages[index];
    while (shouldSkipStage(firstStage)) {
      index++;
      firstStage = appFlowMode.config.stages[index];
    }
    return { stage: firstStage, index: index, previousStage: null };
  }, [shouldSkipStage, appFlowMode.config.stages]);

  React.useEffect(() => {
    // Must wait for account information before we decide which stage to jump to
    if (account) {
      if (currentStage === undefined || needToRestart) {
        setCurrentStage(getFirstStage());
        setNeedToRestart(false);
      } else if (currentStage.stage === AppFlowStage.Done || needToExit) {
        appFlowMode.config.destinations.forEach((destination, index) => {
          // Replace AppFlow on the back stack and continue adding destinations from there
          if (index === 0) {
            history.replace(destination);
          } else {
            history.push(destination);
          }
        });
      }
    }
  }, [
    account,
    getFirstStage,
    setCurrentStage,
    currentStage,
    history,
    appFlowMode.config.destinations,
    needToRestart,
    needToExit,
  ]);

  return (
    <div className="row h-100 mw-100 m-0" style={{ minHeight: "700px" }}>
      <div className="p-0 d-flex flex-column flex-lg-row justify-content-between h-100">
        <div
          className="flex-grow-1 d-flex flex-column justify-content-center h-100"
          style={{ alignItems: "stretch" }}
        >
          {currentStage?.stage === AppFlowStage.Upgrade ? (
            <Upgrade next={() => next()} />
          ) : currentStage?.stage === AppFlowStage.GrownupsHurdle ? (
            <GrownupsHurdle next={() => next()} />
          ) : currentStage?.stage === AppFlowStage.SignUp ? (
            <SignUp account={account} next={() => next()} />
          ) : currentStage?.stage === AppFlowStage.ConfirmationCode ? (
            <ConfirmationCode
              account={account}
              next={() => next()}
              previous={() => previous()}
            />
          ) : currentStage?.stage === AppFlowStage.DataHandling ? (
            <DataHandling next={() => next()} />
          ) : currentStage?.stage === AppFlowStage.PurchaseChoice ? (
            <PurchaseChoice
              next={(selectedProductId) => {
                if (selectedProductId) {
                  setSelectedProductId(selectedProductId);
                }
                next();
              }}
            />
          ) : currentStage?.stage === AppFlowStage.StripeCheckout ? (
            <StripeCheckout
              selectedProductId={selectedProductId}
              existingCheckoutSessionId={existingCheckoutSessionId}
              next={next}
              restartUpgrade={restartUpgrade}
              exitUpgrade={() => setNeedToExit(true)}
            />
          ) : (
            <span>Unsupported Stage</span>
          )}
        </div>
        {currentStage?.stage !== AppFlowStage.PurchaseChoice && (
          <>
            <VerticalContainer className="d-lg-block d-none">
              <img
                className="h-100 w-100"
                alt=""
                style={{
                  objectFit: "cover",
                  objectPosition: "left",
                }}
                src={`${process.env.PUBLIC_URL}/lesson_cover_grid_vertical.jpg`}
              />
            </VerticalContainer>
            <FooterContainer className="d-lg-none d-block">
              <img
                className="img-fluid"
                alt=""
                style={{
                  objectFit: "cover",
                  objectPosition: "top",
                }}
                src={`${process.env.PUBLIC_URL}/lesson_cover_grid.jpg`}
              />
            </FooterContainer>
          </>
        )}
      </div>
    </div>
  );
};
